import {
  mergeDriveWithPreviousMonth,
  parseDriveFile,
  driveRowsToExportSheet,
  type DriveRow,
} from '@/pages/settlement-fees/commissionDriveFormat';
import { extractTemplateFile } from '@/pages/settlement-fees/commissionTemplateBridge';
import {
  parseCommissionFiles,
  type CommissionParseResult,
} from '@/pages/settlement-fees/commissionSheetParser';
import { useCallback, useState } from 'react';
import * as XLSX from 'xlsx';

export interface SelectedCommissionFile {
  id: string;
  name: string;
  size: number;
  file: File;
}

export interface CommissionFileMeta {
  templateLabel?: string;
  previewStatus: 'idle' | 'loading' | 'matched' | 'unmatched' | 'error';
  previewMessage?: string;
}

const isExcelFile = (file: File) => /\.(xlsx|xls)$/i.test(file.name);

export interface CommissionAggregateResult extends CommissionParseResult {
  driveRows?: DriveRow[];
  mergeStats?: { matchedCount: number; appendedCount: number };
}

const previewSingleFile = async (
  file: File,
  referenceYearMonth: string,
): Promise<CommissionFileMeta> => {
  try {
    const templateResult = await extractTemplateFile(file, referenceYearMonth);
    if (templateResult?.rows.length) {
      return {
        previewStatus: 'matched',
        templateLabel: templateResult.templateId,
        previewMessage: `${templateResult.rows.length}행 미리보기`,
      };
    }
    return {
      previewStatus: 'unmatched',
      previewMessage: '템플릿 미매칭',
    };
  } catch {
    return {
      previewStatus: 'error',
      previewMessage: '미리보기 실패',
    };
  }
};

export const useCommissionRateAggregator = () => {
  const [selectedFiles, setSelectedFiles] = useState<SelectedCommissionFile[]>(
    [],
  );
  const [fileMeta, setFileMeta] = useState<Record<string, CommissionFileMeta>>(
    {},
  );
  const [previousMonthFile, setPreviousMonthFile] = useState<File | null>(null);
  const [referenceYearMonth, setReferenceYearMonth] = useState('');
  const [result, setResult] = useState<CommissionAggregateResult | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const runFilePreviews = useCallback(
    async (files: SelectedCommissionFile[], refYm: string) => {
      if (!refYm || refYm.length !== 6) return;
      for (const item of files) {
        setFileMeta((current) => ({
          ...current,
          [item.id]: { previewStatus: 'loading' },
        }));
        const meta = await previewSingleFile(item.file, refYm);
        setFileMeta((current) => ({
          ...current,
          [item.id]: meta,
        }));
      }
    },
    [],
  );

  const addFiles = useCallback(
    (inputFiles: FileList | File[] | null) => {
      if (!inputFiles) return;
      const list = Array.from(inputFiles);
      const added: SelectedCommissionFile[] = [];
      setSelectedFiles((current) => {
        const next = [...current];
        const keys = new Set(
          current.map((f) => `${f.name}-${f.size}-${f.file.lastModified}`),
        );
        list.forEach((file) => {
          if (!isExcelFile(file)) return;
          const key = `${file.name}-${file.size}-${file.lastModified}`;
          if (keys.has(key)) return;
          keys.add(key);
          const entry = { id: key, name: file.name, size: file.size, file };
          next.push(entry);
          added.push(entry);
        });
        return next;
      });
      if (added.length > 0 && referenceYearMonth.length === 6) {
        void runFilePreviews(added, referenceYearMonth);
      }
      setErrorMessage('');
    },
    [referenceYearMonth, runFilePreviews],
  );

  const setComparisonFile = useCallback((file: File | null) => {
    setPreviousMonthFile(file);
    setErrorMessage('');
  }, []);

  const removeFile = useCallback((id: string) => {
    setSelectedFiles((current) => current.filter((f) => f.id !== id));
    setFileMeta((current) => {
      const next = { ...current };
      delete next[id];
      return next;
    });
  }, []);

  const clearAll = useCallback(() => {
    setSelectedFiles([]);
    setFileMeta({});
    setPreviousMonthFile(null);
    setResult(null);
    setErrorMessage('');
  }, []);

  const refreshFilePreviews = useCallback(async () => {
    if (!referenceYearMonth || referenceYearMonth.length !== 6) return;
    await runFilePreviews(selectedFiles, referenceYearMonth);
  }, [referenceYearMonth, runFilePreviews, selectedFiles]);

  const aggregate = useCallback(async () => {
    if (!referenceYearMonth || referenceYearMonth.length !== 6) {
      setErrorMessage('기준년월(YYYYMM)을 입력해 주세요.');
      return null;
    }
    if (selectedFiles.length === 0) {
      setErrorMessage('엑셀 파일을 선택해 주세요.');
      return null;
    }
    if (!previousMonthFile) {
      setErrorMessage(
        '종합 추출 전에 비교파일(이전 월 수수료 종합 xlsx)을 첨부해 주세요. 비교파일은 참고용 비교 기준으로만 사용됩니다.',
      );
      return null;
    }

    setIsLoading(true);
    setErrorMessage('');
    try {
      const parsed = await parseCommissionFiles(
        selectedFiles.map((f) => f.file),
        referenceYearMonth,
      );

      const previousDriveRows = await parseDriveFile(previousMonthFile);
      if (!previousDriveRows?.length) {
        setErrorMessage(
          '비교파일에서 드라이브 포맷을 찾지 못했습니다. 종합 결과 xlsx인지 확인해 주세요.',
        );
        return null;
      }

      const extractedDrive: DriveRow[] = parsed.rows.map((row) => {
        const driveRow = new Array(22).fill('') as DriveRow;
        driveRow[0] = row.referenceYearMonth || referenceYearMonth;
        driveRow[1] = row.pharmaceuticalCompanyName;
        driveRow[2] = row.drugName;
        driveRow[4] = row.drugCode;
        driveRow[5] = String(row.price);
        driveRow[6] = String(row.standardCommissionRate);
        driveRow[7] = row.commissionNote;
        return driveRow;
      });

      const merged = mergeDriveWithPreviousMonth(
        previousDriveRows,
        extractedDrive,
        referenceYearMonth,
      );

      const aggregateResult: CommissionAggregateResult = {
        ...parsed,
        driveRows: merged.rows,
        mergeStats: {
          matchedCount: merged.matchedCount,
          appendedCount: merged.appendedCount,
        },
      };
      setResult(aggregateResult);

      if (parsed.rows.length === 0) {
        setErrorMessage('추출된 행이 없습니다. 헤더 형식을 확인해 주세요.');
      }
      return aggregateResult;
    } catch (e) {
      setErrorMessage(
        e instanceof Error ? e.message : '집계 처리 중 오류가 발생했습니다.',
      );
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [previousMonthFile, referenceYearMonth, selectedFiles]);

  const exportDriveWorkbook = useCallback(() => {
    if (!result?.driveRows?.length) return;
    const workbook = driveRowsToExportSheet(result.driveRows);
    XLSX.writeFile(workbook, `수수료정보종합_${referenceYearMonth}.xlsx`);
  }, [referenceYearMonth, result?.driveRows]);

  const isComparisonFileReady = Boolean(previousMonthFile);
  const isTargetFilesReady = selectedFiles.length > 0;
  // 레거시와 동일: 비교파일 + 추출 대상만 있으면 3단계 잠금 해제 (기준년월은 버튼 disable)
  const isAggregateStepReady = isComparisonFileReady && isTargetFilesReady;

  return {
    selectedFiles,
    fileMeta,
    previousMonthFile,
    setComparisonFile,
    referenceYearMonth,
    setReferenceYearMonth,
    result,
    isLoading,
    errorMessage,
    isComparisonFileReady,
    isTargetFilesReady,
    isAggregateStepReady,
    addFiles,
    removeFile,
    clearAll,
    aggregate,
    exportDriveWorkbook,
    refreshFilePreviews,
  };
};
